/**
 * 把内置高级流程 URL（/wuji/workflow/flow/:flowId?jobId=）解析到用户绑定的 AI 页。
 * 查找表是保存进 artifact / artifactCos.meta.pageBindings 的扁平记录，
 * 契约与 wuji-gogo `WujiPageBindingRecord` / `resolvePageBinding` 对齐。
 */
export function getPageBindingsFromAiProjectInfo(info) {
    const raw = info?.artifact?.meta?.pageBindings ?? info?.artifactCos?.meta?.pageBindings;
    return Array.isArray(raw) ? raw.filter(isAdvancedFlowPageBinding) : [];
}
export function pickJobIdFromRoute(query, params) {
    return firstString(query?.jobId) || firstString(params?.jobId);
}
export function mergeFlowRedirectQuery(incomingQuery, resolvedQuery) {
    const next = { ...(incomingQuery || {}) };
    delete next.jobId;
    return { ...next, ...resolvedQuery };
}
/** 当前流程绑定的发起页、详情页路径。没写 flowId 的详情页绑定对所有流程生效。 */
export function listBoundPagePathsForFlow(pageBindings, flowId) {
    const id = flowId?.trim();
    if (!pageBindings?.length || !id) {
        return [];
    }
    const paths = [];
    const start = pageBindings.find(item => item.type === 'advancedFlowStart' && item.flowId === id);
    if (start?.type === 'advancedFlowStart') {
        paths.push(start.pagePath);
    }
    const exactJob = pageBindings.find(item => item.type === 'advancedFlowJob' && item.flowId === id);
    const fallbackJob = pageBindings.find(item => item.type === 'advancedFlowJob' && item.flowId == null);
    const job = exactJob ?? fallbackJob;
    if (job?.type === 'advancedFlowJob' && !paths.includes(job.pagePath)) {
        paths.push(job.pagePath);
    }
    return paths;
}
/** AI 编辑器 defaultPath：有发起页用发起页，否则用带上 jobId 的流程单页面。 */
export function resolveAdvancedFlowEditorDefaultPath(pageBindings, input) {
    const flowId = input.flowId?.trim();
    if (!pageBindings?.length || !flowId)
        return null;
    const start = resolveAdvancedFlowPageRedirect(pageBindings, { flowId });
    if (start)
        return formatAdvancedFlowRedirectPath(start);
    const jobId = input.jobId?.trim();
    if (jobId) {
        const job = resolveAdvancedFlowPageRedirect(pageBindings, { flowId, jobId });
        if (job)
            return formatAdvancedFlowRedirectPath(job);
    }
    return listBoundPagePathsForFlow(pageBindings, flowId)[0] || null;
}
export function formatAdvancedFlowRedirectPath(redirect) {
    const search = new URLSearchParams(redirect.query).toString();
    return search ? `${redirect.path}?${search}` : redirect.path;
}
export function splitPagePathQuery(raw) {
    const value = raw?.trim() || '';
    const queryIndex = value.indexOf('?');
    const path = queryIndex >= 0 ? value.slice(0, queryIndex) : value;
    const query = {};
    if (queryIndex >= 0) {
        new URLSearchParams(value.slice(queryIndex + 1)).forEach((item, key) => {
            query[key] = item;
        });
    }
    return { path, query };
}
export function pageRouteMatches(routePath, pathname) {
    if (!routePath || !pathname)
        return false;
    if (routePath === pathname)
        return true;
    const source = routePath
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        .replace(/:[A-Za-z0-9_]+/g, '[^/]+');
    return new RegExp(`^${source}$`).test(pathname);
}
export function buildBuiltinFlowRedirect(info, to) {
    return resolveAdvancedFlowPageRedirect(getPageBindingsFromAiProjectInfo(info), {
        flowId: firstString(to.params?.flowId),
        jobId: pickJobIdFromRoute(to.query, to.params),
    });
}
export function resolveAdvancedFlowPageRedirect(pageBindings, input) {
    const flowId = input.flowId?.trim();
    if (!pageBindings?.length || !flowId) {
        return null;
    }
    const jobId = input.jobId?.trim();
    if (jobId) {
        const exact = pageBindings.find((item) => item.type === 'advancedFlowJob' && item.flowId === flowId);
        const fallback = pageBindings.find((item) => item.type === 'advancedFlowJob' && item.flowId == null);
        const hit = exact ?? fallback;
        if (!hit || hit.type !== 'advancedFlowJob') {
            return null;
        }
        const params = {};
        const query = {};
        if (routeParamNames(hit.pagePath).includes('flowId')) {
            params.flowId = flowId;
        }
        if (hit.jobId.to === 'param') {
            params[hit.jobId.key] = jobId;
        }
        else {
            query[hit.jobId.key] = jobId;
        }
        const path = fillRoutePath(hit.pagePath, params);
        if (!path) {
            return null;
        }
        return { path, query };
    }
    const hit = pageBindings.find((item) => item.type === 'advancedFlowStart' && item.flowId === flowId);
    if (!hit) {
        return null;
    }
    const params = {};
    if (routeParamNames(hit.pagePath).includes('flowId')) {
        params.flowId = flowId;
    }
    const path = fillRoutePath(hit.pagePath, params);
    if (!path) {
        return null;
    }
    return { path, query: {} };
}
function isAdvancedFlowPageBinding(raw) {
    if (!raw || typeof raw !== 'object') {
        return false;
    }
    const value = raw;
    if (typeof value.pagePath !== 'string' || !value.pagePath) {
        return false;
    }
    if (value.type === 'advancedFlowStart') {
        return typeof value.flowId === 'string' && !!value.flowId;
    }
    if (value.type !== 'advancedFlowJob') {
        return false;
    }
    const jobId = value.jobId;
    return jobId?.to === 'param' || jobId?.to === 'query'
        ? typeof jobId.key === 'string' && !!jobId.key
        : false;
}
function firstString(raw) {
    if (Array.isArray(raw)) {
        return firstString(raw[0]);
    }
    return raw == null ? '' : String(raw);
}
function routeParamNames(pagePath) {
    return [...pagePath.matchAll(/:([A-Za-z0-9_]+)/g)].map((match) => match[1]);
}
function fillRoutePath(pagePath, params) {
    const names = routeParamNames(pagePath);
    if (names.some((name) => !params[name])) {
        return null;
    }
    return pagePath.replace(/:([A-Za-z0-9_]+)/g, (_, key) => encodeURIComponent(params[key]));
}
